import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Select,
  Popconfirm,
  Switch
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { api } from './api';

interface User {
  id: string;
  email: string;
  nickname: string | null;
  full_name: string | null;
  role: string;
  is_active: boolean;
  group_id: string | null;
  group_name: string | null;
}

interface Group {
  id: string;
  name: string;
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm] = Form.useForm();
  const [createForm] = Form.useForm();

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/api/groups/admin/users');
      if (!res.ok) throw new Error('Kunne ikke hente brugerliste');
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente brugerliste');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const res = await api('/api/groups');
      if (!res.ok) throw new Error('Kunne ikke hente grupper');
      const data = await res.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente grupper');
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadGroups();
  }, [loadUsers, loadGroups]);

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      const values = await editForm.validateFields();
      const res = await api(`/api/groups/admin/users/${selectedUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: values.full_name ? values.full_name.trim() : null,
          nickname: values.nickname ? values.nickname.trim() : null,
          role: values.role,
          is_active: values.is_active
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere bruger');
      }

      message.success('Bruger opdateret');
      setEditModalOpen(false);
      editForm.resetFields();
      await loadUsers();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.message || 'Kunne ikke opdatere bruger');
    }
  };

  const handleOpenEdit = (user: User) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      full_name: user.full_name || '',
      nickname: user.nickname || '',
      role: user.role,
      is_active: user.is_active
    });
    setEditModalOpen(true);
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const res = await api(`/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke slette bruger');
      }

      message.success('Bruger slettet');
      await loadUsers();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke slette bruger');
    }
  };

  const handleCreateUser = async () => {
    try {
      const values = await createForm.validateFields();
      const res = await api('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.email.trim(),
          password: values.password,
          full_name: values.full_name ? values.full_name.trim() : null,
          nickname: values.nickname ? values.nickname.trim() : null,
          role: values.role || 'REFERRER',
          is_active: true
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette bruger');
      }

      message.success('Bruger oprettet');
      setCreateModalOpen(false);
      createForm.resetFields();
      await loadUsers();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.message || 'Kunne ikke oprette bruger');
    }
  };

  const columns = [
    {
      title: 'Navn',
      render: (_: any, record: User) => record.full_name || record.email
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Brugernavn',
      dataIndex: 'nickname',
      key: 'nickname',
      render: (nickname: string | null) => nickname || '—'
    },
    {
      title: 'Rolle',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => <Tag>{role}</Tag>
    },
    {
      title: 'Gruppe',
      dataIndex: 'group_name',
      key: 'group_name',
      render: (group_name: string | null) => group_name || '—'
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active: boolean) => (
        <Tag color={is_active ? 'green' : 'red'}>{is_active ? 'Aktiv' : 'Inaktiv'}</Tag>
      )
    },
    {
      title: 'Handlinger',
      key: 'actions',
      width: 100,
      render: (_: any, record: User) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)} />
          <Popconfirm
            title="Slet bruger?"
            description="Er du sikker?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Ja"
            cancelText="Nej"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card title="Brugerstyring" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>Ny bruger</Button>}>
      <Table columns={columns} dataSource={users} loading={loading} rowKey="id" pagination={false} />

      <Modal
        title="Opret bruger"
        open={createModalOpen}
        onOk={handleCreateUser}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
        }}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email er påkrævet' },
              { type: 'email', message: 'Ugyldig email-adresse' }
            ]}
          >
            <Input placeholder="bruger@eksempel.dk" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Adgangskode"
            rules={[
              { required: true, message: 'Adgangskode er påkrævet' },
              { min: 6, message: 'Adgangskode skal være mindst 6 tegn' }
            ]}
          >
            <Input.Password placeholder="Mindst 6 tegn" />
          </Form.Item>
          <Form.Item name="full_name" label="Navn">
            <Input placeholder="Fulde navn" />
          </Form.Item>
          <Form.Item name="nickname" label="Brugernavn (login)">
            <Input placeholder="Kan bruges til login i stedet for email" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Rolle"
            rules={[{ required: true, message: 'Vælg rolle' }]}
          >
            <Select
              options={[
                { label: 'BROKER_ADMIN', value: 'BROKER_ADMIN' },
                { label: 'BROKER', value: 'BROKER' },
                { label: 'GROUPE_ADMIN', value: 'GROUPE_ADMIN' },
                { label: 'REFERRER', value: 'REFERRER' }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Rediger bruger"
        open={editModalOpen}
        onOk={handleUpdateUser}
        onCancel={() => {
          setEditModalOpen(false);
          editForm.resetFields();
          setSelectedUser(null);
        }}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="full_name" label="Navn">
            <Input placeholder="Fulde navn" />
          </Form.Item>
          <Form.Item name="nickname" label="Brugernavn (login)">
            <Input placeholder="Kan bruges til login i stedet for email" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Rolle"
            rules={[{ required: true, message: 'Vælg rolle' }]}
          >
            <Select
              options={[
                { label: 'BROKER_ADMIN', value: 'BROKER_ADMIN' },
                { label: 'BROKER', value: 'BROKER' },
                { label: 'GROUPE_ADMIN', value: 'GROUPE_ADMIN' },
                { label: 'REFERRER', value: 'REFERRER' }
              ]}
            />
          </Form.Item>
          <Form.Item name="is_active" label="Aktiv" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
